<template>
  <div v-if="scenario" class="page-scenarios">
    <header class="banner">
      <div class="content flex flex-col items-center justify-center">
        <h1>
          <div>
            {{ scenario?.title }}
          </div>
          <div>
            {{ scenario?.summarize }}
          </div>
        </h1>
      </div>
    </header>

    <article class="page-container">
      <section
        v-if="scenario && get(scenario, 'config', []).length > 1"
        class="module-tabs sticky top-[0px]"
      >
        <Tabs v-model:active="activeTab" :tabList="get(scenario, 'config', [])" />
      </section>
      <section
        v-for="(item, idx) in currentScenario?.list"
        :key="idx + item.type + item.title + item.desc + item.id || ''"
        :class="['module-item mb-[80px]', item.type]"
      >
        <template v-if="item.type === 'VideoCard'">
          <VideoCard :data="item" :revert="idx % 2 === 0" />
        </template>
        <template v-if="item.type === 'EnterpriseCard'">
          <EnterpriseCard :data="item" />
        </template>
        <template v-if="item.type === 'TemplateCard'">
          <TemplateCard
            :data="item as unknown as ScenariosTemplateConfig"
            :filteredTemplates="filteredTemplates"
          />
        </template>
        <template v-if="item.type === 'CustomerVoice'">
          <CustomerVoiceCard :data="item" />
        </template>
      </section>
    </article>

    <footer v-if="isChinese()" class="mb-[80px] relative flex-center">
      <QrFooter />
    </footer>
  </div>
</template>
<script lang="ts" setup>
import { get } from 'lodash-es';
import type { ScenariosTemplateConfig } from '~/api/interface';
import { getScenariosDetail } from '~/api/module/scenarios';
import { PageKey } from '~/constants/page';
import { useWatchPageSeoDataFallback } from '~/hooks/useSeo';
import { isChinese } from '~/utils/area';
import CustomerVoiceCard from './components/customer-voice-card/index.vue';
import EnterpriseCard from './components/enterprise-card/index.vue';
import QrFooter from './components/qr-footer.vue';
import Tabs from './components/tabs.vue';
import TemplateCard from './components/template-card/index.vue';
import VideoCard from './components/video-card/index.vue';

provideResourceLocation({
  source: SOURCE.SCENARIOS,
});

const route = useRoute();
const router = useRouter();

const { data: scenario } = await useAsyncData('scenarios-new', async () => {
  const res = await getScenariosDetail({
    tag_outer_id: String(route.params.id),
  });

  const scenario = get(res, 'data[0]');

  if (!scenario || !scenario.config) {
    router.replace({ name: PageKey.NOT_FOUND });
  }

  return scenario;
});

const activeTab = ref(0);

const currentScenario = computed(() => {
  return get(scenario.value, `config[${activeTab.value}]`);
});

const filteredTemplates = computed(() => {
  return (
    currentScenario.value?.list.filter(item => item.type === 'VideoCard').map(v => v.template.id) ||
    []
  );
});

const pageSeoData = computed(() => {
  return {
    seo_title: scenario.value?.title,
    seo_description: scenario.value?.summarize,
  };
});

useWatchPageSeoDataFallback(pageSeoData);
</script>
<style lang="scss" scoped>
.VideoCard + .VideoCard {
  margin-top: -40px;
}
.page-scenarios {
  section {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    &.module-tabs {
      margin-bottom: 40px;
      z-index: var(--layer-sticky);
      top: 20px;
    }
  }
  .banner {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 240px;
    background-size: cover;
    .content {
      position: relative;
      color: var(--purple-10);
      width: 1200px;
      margin: 0 auto;
      text-align: center;

      display: flex;
      .left {
        width: 640px;
      }
      .right {
        flex: 1;
      }
      h1 {
        font-size: 40px;
        font-weight: 600;
        line-height: 64px;
        padding: 80px 64px;
        margin: 0;
      }
      p {
        font-size: 14px;
        font-weight: 400;
        line-height: 28px;
        margin: 0;
      }
      .create-btn {
        position: absolute;
        top: 50%;
        right: 0;
        transform: translateY(-50%);
      }
    }
  }
  .page-container {
    width: 1200px;
    margin: 0 auto;
  }
}
</style>
